import { CORE_DEPARTMENTS } from '../pages/core/coverSheet';
import { Status } from '../../types';
import { getOverviewStatus } from '../../utils/config-utils';
import type { Section, Subsection } from '../types';
import formConfig from './secondments';

const getSubsection = (slug: string) =>
  formConfig.sections.flatMap((section) => section.subsections).find((item) => item.slug === slug);

describe('secondments config', () => {
  it('provides guidance, inward, outward, and further-question sections', () => {
    expect(formConfig).toEqual(
      expect.objectContaining({
        live: true,
        id: 'secondments',
        formName: 'Secondments data return',
        title: 'Secondments data return',
        identifier: '{secondments-department}',
        listBulkUpload: true,
        listBulkUploadMode: 'direct',
      }),
    );
    expect(formConfig.sections.map(({ title }) => title)).toEqual([
      'Section 1',
      'Inward secondments details',
      'Outward Secondments Details',
      'Further Questions',
    ]);
  });

  it('hides optional labels on every question', () => {
    const questions = formConfig.sections
      .flatMap((section) => section.subsections)
      .flatMap((subsection) => subsection.pages)
      .flatMap((page) => (page.pageType === 'information' ? [] : (page.questions ?? [])));

    expect(questions.length).toBeGreaterThan(0);
    expect(questions.every(({ hideOptionalLabel }) => hideOptionalLabel === true)).toBe(true);
  });

  it('shows complete commission guidance before department and contact email', () => {
    const pages = getSubsection('guidance-and-contact-details')?.pages ?? [];
    expect(pages.map(({ name }) => name)).toEqual([
      'commission-guidance',
      'department-and-contact-email',
    ]);

    expect(pages[0]).toEqual(
      expect.objectContaining({
        pageType: 'information',
        title: 'Commission Guidance',
        titleClassName: 'govuk-!-margin-bottom-4',
      }),
    );
    expect(pages[0].description).toContain('<strong>Scope</strong>');
    expect(pages[0].description).toContain('<strong>Instructions</strong>');
    expect(
      pages[0].description
        ?.split('<strong>Instructions</strong>')[1]
        .split('<strong>Context</strong>')[0]
        .trim(),
    ).toBe(
      `For this secondments data commission, please include all secondments which were active at some point between 1 October 2025 and 31 March 2026.
Please provide detailed data for these secondments (if you hold this) in the 'Inward Secondment Details' and 'Outward Secondment Details' sections.
If you only have partial information, please still input this in the 'Inward Secondment Details' and 'Outward Secondment Details' tabs.
This information will populate a summary table. If this is incorrect and you are aware of more inward and outward secondments that you don’t have full details for, you can edit this table. Please ensure this final table is accurate and aligns with your records.`,
    );
    expect(pages[0].description).toContain('<strong>Context</strong>');
    expect(pages[0].description).toContain('<strong>Contact us</strong>');
    expect(pages[0].description).toContain('secondmentexpertise@cabinetoffice.gov.uk');
    expect(pages[0].description).not.toContain('<br><br>');
    expect(pages[0].descriptionClassName).toBe('govuk-body-s');

    const contactQuestions = pages[1].pageType === 'question' ? (pages[1].questions ?? []) : [];
    expect(contactQuestions).toEqual([
      expect.objectContaining({
        name: 'secondments-department',
        questionType: 'select',
        options: expect.arrayContaining([
          ...CORE_DEPARTMENTS,
          'AGO/GLD',
          'DLUHC',
          'FAST STREAM',
          'FSA',
          'NIO',
          'SCOT GOV',
          'UKEF',
          'UKSA/ONS',
          'WELSH GOV',
        ]),
        rules: { required: true },
      }),
      expect.objectContaining({
        name: 'secondments-contact-email',
        questionType: 'textInput',
        userEmail: true,
        rules: { required: true, email: true },
      }),
    ]);
    expect(contactQuestions[0].options).toHaveLength(26);
    expect(CORE_DEPARTMENTS).toHaveLength(17);
  });

  it.each([
    ['inward', 'from'],
    ['outward', 'to'],
  ])('configures %s details, editable grade summary, and optional notes', (direction, movement) => {
    const pages = getSubsection(`${direction}-secondments-details`)?.pages ?? [];
    expect(pages).toHaveLength(2);

    const detailQuestions = pages[0].pageType === 'question' ? (pages[0].questions ?? []) : [];
    const detailTable = detailQuestions[0];
    expect(detailTable).toEqual(
      expect.objectContaining({
        name: `${direction}-secondments-details`,
        questionType: 'dynamicTable',
        rules: { required: false },
      }),
    );
    expect(detailTable.dynamicTable).toEqual(
      expect.objectContaining({
        rowHeader: 'Secondment',
        showRowHeader: false,
        continuous: true,
        initialContinuousEditableRowCount: 5,
        rows: [],
        headerLineLimit: 2,
        compact: true,
        stickyHeaders: true,
        highlightActive: true,
        showExcelToolbar: true,
        confirmExcelImport: false,
      }),
    );
    expect(detailTable.dynamicTable?.columns).toEqual([
      expect.objectContaining({
        key: 'grade',
        inputType: 'select',
        options: ['AO', 'EO', 'HEO', 'SEO', 'G7', 'G6', 'SCS', 'Other', 'Unknown'],
        emptyOptionLabel: '',
      }),
      expect.objectContaining({
        key: 'professionFunction',
        inputType: 'text',
        headerLineLimit: 1,
      }),
      expect.objectContaining({ key: 'role', inputType: 'text', minWidth: 360 }),
      expect.objectContaining({
        key: `organisationSeconded${movement === 'from' ? 'From' : 'To'}`,
        inputType: 'text',
        fillRemainingWidth: true,
      }),
      expect.objectContaining({
        key: 'expectedDuration',
        inputType: 'duration',
        duration: expect.objectContaining({
          unitKey: 'expectedDurationUnit',
          durationDaysKey: 'secondment_duration',
          defaultUnit: 'day(s)',
        }),
      }),
      expect.objectContaining({
        key: 'commencedDuringReportingPeriod',
        inputType: 'select',
        headerLineLimit: 3,
        options: ['Yes', 'No'],
      }),
    ]);

    const summaryQuestions = pages[1].pageType === 'question' ? (pages[1].questions ?? []) : [];
    expect(summaryQuestions[0].dynamicTable).toEqual(
      expect.objectContaining({
        rowCountsFrom: {
          question: `${direction}-secondments-details`,
          sourceColumn: 'grade',
          targetColumn: 'numberOfCurrentSecondments',
          sourceValueByRow: { otherGrade: 'Other' },
        },
      }),
    );
    expect(summaryQuestions[0].dynamicTable?.rows.map(({ label }) => label)).toEqual([
      'SCS',
      'G6',
      'G7',
      'SEO',
      'HEO',
      'EO',
      'AO',
      'Other Grade',
      'Unknown',
    ]);
    expect(summaryQuestions[1]).toEqual(
      expect.objectContaining({
        name: `${direction}-secondments-notes`,
        questionType: 'textarea',
        rules: { required: false },
      }),
    );
  });

  it.each(['inward', 'outward'] as const)(
    'marks the %s subsection completed when its saved summary contains only zeroes',
    (direction) => {
      const slug = `${direction}-secondments-details`;
      const configuredSubsection = getSubsection(slug)!;
      const subsection: Subsection = { ...configuredSubsection, dependsOn: undefined };
      const section: Section = {
        title: `${direction} secondments`,
        slug: `${direction}-secondments`,
        subsections: [subsection],
      };

      expect(
        getOverviewStatus([section], { [slug]: subsection }, {
          answers: {
            [`${direction}-secondments-summary`]: JSON.stringify({
              scsnumberOfCurrentSecondments: '0',
            }),
          },
        })[slug],
      ).toBe(Status.Completed);
    },
  );

  it('matches the six workbook further questions', () => {
    const pages = getSubsection('further-questions')?.pages ?? [];
    const questions = pages[0].pageType === 'question' ? (pages[0].questions ?? []) : [];

    expect(questions).toHaveLength(6);
    expect(questions.every(({ questionType }) => questionType === 'textarea')).toBe(true);
    expect(questions.every(({ rules }) => rules?.required === true)).toBe(true);
    expect(questions.map(({ title }) => title)).toEqual([
      'Do you have an aspiration or planned number of inward or outward secondments in the next 12 months?',
      'Do you envisage any changes in your current approach to secondments?',
      'Are there particular capability gaps you plan to fill through secondments?',
      'Are there particular sectors or organisations that you plan to engage with on secondments?',
      'Are you encountering any particular barriers or challenges in setting up or maintaining secondments?',
      'Does your department currently conduct, take part in or know of any Secondment Schemes/Initiatives?',
    ]);
  });
});
